import { inject, Injectable } from "@angular/core";
import { FgdsProductType, FullProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ProductData } from "@domain/project/product-data/product-data";
import { ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { ProductItem } from "@project/product-item";
import { ALPHABET } from "@utils/alphabet";
import { catchError, defaultIfEmpty, forkJoin, map, Observable, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AttachmentDataService {
  private productService = inject(ProductService);

  getProductData(
    productConfiguration: ProductConfiguration,
    attachments: ProductItem[],
    localeId: string,
    positionNumberOffset: number = 0,
  ): Observable<ProductData[]> {
    if (!attachments.length) {
      return of([]);
    }
    return this.getProducts(this.getAttachmentProductIds(attachments)).pipe(
      map((attachmentProducts) => {
        const attachmentProductsWithQuantity = attachmentProducts.reduce(
          (acc, product, index) => {
            acc.push({
              product,
              quantity: attachments[index].quantity,
            });
            return acc;
          },
          [] as { product: Product; quantity: number }[],
        );

        let attachmentData = ProductData.mapAttachmentsAsProductData(
          attachmentProductsWithQuantity,
          localeId,
          this.productService.getFilterCountry()!,
          ProductListDiscountSlot.ATTACHMENT,
        );

        const attachmentInstructions = attachmentData.filter(
          (attachment) => attachment.fullType === FullProductType.ATTACHMENT_INSTRUCTIONS,
        );
        const defaultAttachments = attachmentData.filter(
          (attachment) => attachment.fullType !== FullProductType.ATTACHMENT_INSTRUCTIONS,
        );
        attachmentData = [...defaultAttachments, ...attachmentInstructions];

        attachmentData.forEach((attachment: ProductData, index) => {
          attachment.positionId = productConfiguration.positionNumber! + "." + ALPHABET[index + positionNumberOffset];
        });
        return attachmentData;
      }),
    );
  }

  public getAttachmentProductIds(attachments: ProductItem[]) {
    return attachments.map((attachment) => attachment.productId);
  }

  private getProducts(attachmentIds: string[]): Observable<Product[]> {
    if (!attachmentIds.length) {
      return of([]);
    }

    const products = attachmentIds.map((attachmentId) =>
      this.productService.getProductById(attachmentId).pipe(
        map((attachment) => (attachment && attachment.type === FgdsProductType.ATTACHMENT ? attachment : null)),
        defaultIfEmpty(undefined),
        catchError(() => of(undefined)),
      ),
    );

    return forkJoin(products).pipe(map((results) => results.filter((p): p is Product => !!p)));
  }
}
